import { Head, router, usePage } from "@inertiajs/react"
import { ClipboardList, SlidersHorizontal } from "lucide-react"
import { useState } from "react"

import LocationSelect from "@/components/location-select"
import TryoutCard from "@/components/tryouts/tryout-card"
import { Button } from "@/components/ui/button"
import AppLayout from "@/layouts/app-layout"
import type { TryoutsIndex } from "@/types/serializers"

export default function TryoutsIndex({
  tryouts,
  pagination,
  filters,
}: TryoutsIndex) {
  const page = usePage()
  const auth = page.props.auth as { user: { role: string } | null }
  const userRole = auth?.user?.role
  const [country, setCountry] = useState(filters.country ?? "")
  const [city, setCity] = useState(filters.city ?? "")

  function applyFilters(newFilters: Partial<TryoutsIndex["filters"]>) {
    const merged = { ...filters, ...newFilters }
    const params: Record<string, string> = {}
    for (const [key, value] of Object.entries(merged)) {
      if (value) params[key] = value
    }
    router.visit("/pruebas", { data: params, preserveState: true })
  }

  function handleCountryChange(value: string) {
    setCountry(value)
    setCity("")
    applyFilters({ country: value, city: "" })
  }

  function handleRegionChange() {
    // region not used as filter
  }

  function handleCityChange(value: string) {
    setCity(value)
    applyFilters({ city: value })
  }

  function clearFilters() {
    setCountry("")
    setCity("")
    router.visit("/pruebas", { preserveState: true })
  }

  return (
    <AppLayout>
      <Head title="Pruebas | KrownFT" />

      <div className="bg-background min-h-svh">
        <div className="mx-auto max-w-6xl px-4 py-6">
          <div className="mb-6 flex items-center justify-between">
            <h1 className="text-2xl font-bold text-white">Pruebas</h1>
            {userRole === "club" && (
              <Button onClick={() => router.visit("/pruebas/new")}>
                Crear prueba
              </Button>
            )}
          </div>

          <div className="flex flex-col gap-6 lg:flex-row">
            <aside className="w-full shrink-0 lg:w-64">
              <div className="border-border bg-card rounded-lg border p-4">
                <div className="mb-4 flex items-center gap-2">
                  <SlidersHorizontal className="h-4 w-4" />
                  <h2 className="font-semibold">Filtros</h2>
                </div>

                <div className="space-y-4">
                  <LocationSelect
                    country={country}
                    region=""
                    city={city}
                    onCountryChange={handleCountryChange}
                    onRegionChange={handleRegionChange}
                    onCityChange={handleCityChange}
                    showCity={true}
                  />

                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={clearFilters}
                  >
                    Limpiar filtros
                  </Button>
                </div>
              </div>
            </aside>

            <div className="flex-1">
              <p className="text-muted-foreground mb-4 text-sm">
                {pagination.count === 1
                  ? "1 prueba encontrada"
                  : `${pagination.count} pruebas encontradas`}
              </p>

              {tryouts.length > 0 ? (
                <div className="space-y-4">
                  {tryouts.map((tryout) => (
                    <TryoutCard key={tryout.id} tryout={tryout} />
                  ))}
                </div>
              ) : (
                <div className="py-12 text-center">
                  <ClipboardList className="text-krown-grey-50 mx-auto mb-4 h-12 w-12" />
                  <p className="text-muted-foreground">
                    No hay pruebas disponibles en este momento.
                  </p>
                </div>
              )}

              {pagination.pages > 1 && (
                <div className="mt-6 flex justify-center gap-2">
                  {Array.from(
                    { length: pagination.pages },
                    (_, i) => i + 1,
                  ).map((p) => (
                    <Button
                      key={p}
                      variant={p === pagination.page ? "default" : "outline"}
                      size="sm"
                      onClick={() =>
                        applyFilters({ ...filters, page: String(p) })
                      }
                    >
                      {p}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  )
}
